import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Spinner, Select } from '../../components';
import type { SelectOption } from '../../components';
import { Cross2Icon, PersonIcon, PlusIcon, BookmarkIcon } from '@radix-ui/react-icons';
import { CreateLeadRequest, LeadSourceOption, LeadStatus } from '../../types/lead';
import { useLeadStore } from '../../store/leadStore';
import { useToastStore } from '../../store/toastStore';
import { leadService } from '../../services/leadService';
import { formatApiError } from '../../utils/errorHandler';
import { parsePhoneNumber } from '../users/profileFormConstants';
import { validateContactPhoneNumber } from '../users/profileFormValidation';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { LeadCustomerDetailsSection } from './components/LeadCustomerDetailsSection';
import { LeadFollowUpSection } from './components/LeadFollowUpSection';
import { LeadNotesSection } from './components/LeadNotesSection';
import {
  INITIAL_LEAD_FORM,
  LeadFormData,
  getFullPhoneFromForm,
  getPhoneValidationError,
  isLeadFormValid,
  mapFormToCreatePayload,
  mapFormToUpdatePayload,
  mapLeadToFormData,
} from './leadFormUtils';
import './leadFormShared.scss';
import './LeadFormPanel.scss';

export type LeadFormPanelMode = 'create' | 'edit' | 'follow_up';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'new_lead', label: 'New Lead' },
  { value: 'lead', label: 'Lead' },
  { value: 'progress', label: 'In Progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'pending', label: 'Pending' },
  { value: 'converted', label: 'Converted' },
];

const STATUS_LABELS: Record<LeadStatus, string> = {
  new_lead: 'New Lead',
  lead: 'Lead',
  progress: 'In Progress',
  qualified: 'Qualified',
  pending: 'Pending',
  converted: 'Converted',
};

export interface LeadFormPanelProps {
  isOpen: boolean;
  mode: LeadFormPanelMode;
  leadId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const LeadFormPanel: React.FC<LeadFormPanelProps> = ({
  isOpen,
  mode,
  leadId,
  onClose,
  onSuccess,
}) => {
  const isEditMode = mode === 'edit';
  const isFollowUpMode = mode === 'follow_up';
  const isExistingLeadMode = isEditMode || isFollowUpMode;
  const { createLead, fetchLeads, isLoading, error, clearError, leadSources, fetchLeadSources } =
    useLeadStore();
  const showErrorMessage = useToastStore((state) => state.showErrorMessage);

  const [formData, setFormData] = useState<LeadFormData>(INITIAL_LEAD_FORM);
  const [initialFormData, setInitialFormData] = useState<LeadFormData>(INITIAL_LEAD_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sources, setSources] = useState<LeadSourceOption[]>(leadSources);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const {
    error: emailError,
    onBlur: onEmailBlur,
    onChange: onEmailChange,
    validate: validateEmailField,
    reset: resetEmailValidation,
  } = useEmailValidation();

  const resetForm = useCallback(() => {
    setFormData(INITIAL_LEAD_FORM);
    setInitialFormData(INITIAL_LEAD_FORM);
    setLoadError(null);
    setPhoneTouched(false);
    resetEmailValidation();
    clearError();
  }, [clearError, resetEmailValidation]);

  const fullPhoneNumber = useMemo(() => getFullPhoneFromForm(formData), [formData]);

  const phoneError = useMemo(
    () => getPhoneValidationError(fullPhoneNumber, phoneTouched),
    [fullPhoneNumber, phoneTouched]
  );

  const isStatusReadOnly = isExistingLeadMode && formData.status === 'converted';

  useEffect(() => {
    if (!isOpen) return;
    if (leadSources.length === 0) {
      fetchLeadSources().then(() => {
        setSources(useLeadStore.getState().leadSources);
      });
    } else {
      setSources(leadSources);
    }
  }, [isOpen, leadSources, fetchLeadSources]);

  const sourceOptions: SelectOption[] = useMemo(
    () =>
      sources.map((source) => ({
        value: String(source.id),
        label: source.name,
      })),
    [sources]
  );

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (!isExistingLeadMode || !leadId) {
      resetForm();
      return;
    }

    let cancelled = false;
    (async () => {
      setIsLoadingLead(true);
      setLoadError(null);
      try {
        const lead = await leadService.getLeadById(leadId);
        if (!cancelled) {
          const mapped = mapLeadToFormData(lead);
          setFormData(mapped);
          setInitialFormData(mapped);
        }
      } catch (err) {
        if (!cancelled) {
          const message = formatApiError(err).trim() || 'Unable to load lead.';
          setLoadError(message);
          showErrorMessage({
            title: 'Failed to load lead',
            message,
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingLead(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isExistingLeadMode, leadId, resetForm, showErrorMessage]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleInputChange = useCallback(
    <K extends keyof LeadFormData>(field: K, value: LeadFormData[K]) => {
      clearError();
      setLoadError(null);
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [clearError]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      const { countryCode, localNumber } = parsePhoneNumber(value);
      clearError();
      setLoadError(null);
      setFormData((prev) => ({
        ...prev,
        phoneE164: value,
        phoneCountryCode: countryCode,
        phoneNumber: localNumber,
      }));
    },
    [clearError]
  );

  const isFormValid = useMemo(
    () => (isFollowUpMode ? Boolean(leadId) : isLeadFormValid(formData, fullPhoneNumber)),
    [formData, fullPhoneNumber, isFollowUpMode, leadId]
  );

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;

    if (!isFollowUpMode) {
      const emailResult = validateEmailField(formData.email);
      if (!emailResult.valid) {
        return;
      }

      setPhoneTouched(true);
      if (fullPhoneNumber && validateContactPhoneNumber(fullPhoneNumber)) {
        return;
      }
    }

    setIsSaving(true);
    try {
      if (isExistingLeadMode && leadId) {
        const emailResult = validateEmailField(formData.email);
        const diffPayload = mapFormToUpdatePayload(
          formData,
          initialFormData,
          emailResult.trimmed
        );
        if (Object.keys(diffPayload).length > 0) {
          await leadService.updateLead(leadId, diffPayload);
        }
        await fetchLeads();
      } else {
        const emailResult = validateEmailField(formData.email);
        const payload = mapFormToCreatePayload(formData, emailResult.trimmed);
        await createLead(payload as CreateLeadRequest);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      const message = formatApiError(err).trim() || 'Unable to save lead.';
      setLoadError(message);
      showErrorMessage({
        title: isFollowUpMode
          ? 'Failed to save follow-up'
          : isEditMode
            ? 'Failed to update lead'
            : 'Failed to create lead',
        message,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    isFormValid,
    formData,
    initialFormData,
    fullPhoneNumber,
    validateEmailField,
    isExistingLeadMode,
    isFollowUpMode,
    leadId,
    createLead,
    fetchLeads,
    isEditMode,
    onClose,
    onSuccess,
    showErrorMessage,
  ]);

  if (!isOpen) return null;

  return (
    <div className="lead-form-modal" role="presentation">
      <div
        className="lead-form-modal__overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="lead-form-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-form-modal-title"
      >
        <header className="lead-form-modal__header">
          <div>
            <h2 id="lead-form-modal-title" className="lead-form-modal__title">
              {isFollowUpMode ? 'Follow Up on Lead' : isEditMode ? 'Edit Lead' : 'Add New Lead'}
            </h2>
            <p className="lead-form-modal__subtitle">
              {isFollowUpMode
                ? 'Update pipeline status, follow-up remarks, and notes for this lead.'
                : isEditMode
                ? 'Update lead details across customer profile and follow-up fields.'
                : 'Enter lead details matching the client sheet layout.'}
            </p>
          </div>
          <button
            type="button"
            className="lead-form-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <Cross2Icon />
          </button>
        </header>

        {(error || loadError) && (
          <div className="lead-form-modal__error">
            {loadError || error?.message}
          </div>
        )}

        {isLoadingLead ? (
          <div className="lead-form-modal__loading">
            <Spinner size="medium" />
          </div>
        ) : (
          <>
            <div className="lead-form-modal__body">
              {!isFollowUpMode && (
              <LeadCustomerDetailsSection
                formData={formData}
                sourceOptions={sourceOptions}
                emailError={emailError}
                phoneError={phoneError}
                isStatusReadOnly={isStatusReadOnly}
                onFieldChange={handleInputChange}
                onEmailChange={onEmailChange}
                onEmailBlur={onEmailBlur}
                onPhoneChange={handlePhoneChange}
                onPhoneBlur={() => setPhoneTouched(true)}
              />
              )}
              {isFollowUpMode && (
                <section className="lead-form-shared__section">
                  <h3 className="lead-form-shared__section-title">Pipeline status</h3>
                  <div className="lead-form-modal__row">
                    <div className="lead-form-modal__field">
                      <label className="lead-form-modal__label">Status</label>
                      {isStatusReadOnly ? (
                        <span className="lead-form-shared__status-badge">
                          {STATUS_LABELS[formData.status]}
                        </span>
                      ) : (
                        <div className="lead-form-modal__select-wrap">
                          <BookmarkIcon className="lead-form-modal__input-icon" aria-hidden="true" />
                          <Select
                            value={formData.status === 'converted' ? 'qualified' : formData.status}
                            onChange={(value) => handleInputChange('status', value as LeadStatus)}
                            options={STATUS_OPTIONS}
                            searchable={false}
                            className="lead-form-modal__select"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}
              <LeadFollowUpSection formData={formData} onFieldChange={handleInputChange} />
              <LeadNotesSection formData={formData} onFieldChange={handleInputChange} />
            </div>

            <footer className="lead-form-modal__footer">
              <button
                type="button"
                className="lead-form-modal__cancel"
                onClick={onClose}
                disabled={isSaving || isLoading}
              >
                Cancel
              </button>
              <Button
                onClick={handleSubmit}
                disabled={isSaving || isLoading || !isFormValid}
                className="lead-form-modal__submit"
              >
                {isSaving ? (
                  <Spinner size="small" />
                ) : (
                  <>
                    <PersonIcon />
                    <PlusIcon />
                  </>
                )}
                {isSaving
                  ? 'Saving...'
                  : isEditMode
                    ? 'Save Changes'
                    : 'Create Lead'}
              </Button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};
